import clsx from "clsx";
import { useId, forwardRef, KeyboardEvent } from "react";
import FieldLabel from "../FieldLabel/FieldLabel";
import { NumericFormat } from "react-number-format";

interface ITextInput {
  label?: string;
  placeholder?: string;
  id?: string;
  required?: boolean;
  value?: string;
  onChange: (e: any) => void;
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  list?: string;
  fontBold?: boolean;
  form?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  autoComplete?: string;
  step?: number;
  inputMode?:
    | "text"
    | "numeric"
    | "tel"
    | "email"
    | "url"
    | "search"
    | "none"
    | "decimal"
    | undefined;
  onEnter?: (e: KeyboardEvent<HTMLInputElement>) => void;
  helpText?: string;
  currencySymbol?: string;
  min?: number;
  max?: number;
}

const TextInput = forwardRef<HTMLInputElement, ITextInput>(
  (
    {
      label,
      placeholder,
      id,
      required,
      value = "",
      onChange,
      onKeyDown = () => {},
      type = "text",
      disabled = false,
      readOnly = false,
      autoFocus = false,
      list = "",
      fontBold = false,
      form = "",
      className = "",
      autoComplete,
      inputMode = "",
      step = 0.1,
      onEnter,
      helpText,
      currencySymbol = "",
      min = null,
      max = null,
    },
    ref,
  ) => {
    const inputAutoId = useId();

    const getInputMode = () => {
      if (inputMode) return inputMode;

      switch (type) {
        case "number":
          return "numeric";
        case "tel":
          return "tel";
        case "email":
          return "email";
        case "url":
          return "url";
        default:
          return "text";
      }
    };

    return (
      <div className="flex flex-col w-full">
        {label && (
          <FieldLabel
            required={required}
            text={label}
            htmlFor={id || inputAutoId}
            valid={!!value}
          />
        )}

        {type === "number" ? (
          <NumericFormat
            getInputRef={ref}
            value={value}
            prefix={currencySymbol}
            id={id || inputAutoId}
            placeholder={placeholder}
            required={required}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            onValueChange={(values) => {
              onChange({
                target: {
                  value: values.floatValue ?? "",
                },
              });
            }}
            disabled={disabled}
            readOnly={readOnly}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e?.key === "Enter") {
                onEnter?.(e);
              }
              onKeyDown?.(e);
            }}
            className={clsx([
              "dark:text-white",
              "border-solid",
              "border-b-2",
              "text-xs",
              "md:text-sm",
              "w-full",
              "h-[40px]",
              // "rounded",
              "focus:outline-none",
              "placeholder:text-xs",
              "md:placeholder:text-sm",
              "placeholder:font-light",
              "appearance-none",
              "min-w-full",
              "bg-transparent",
              "dark:[color-scheme:dark]",
              "px-1",
              "md:px-2",
              {
                "border-red-200 dark:border-teal-500": required && !value,
                "border-gray-200 dark:border-gray-600":
                  (required && value) || !required,
                "font-light": !fontBold,
              },
              {
                "placeholder:text-gray-500 dark:placeholder:text-gray-300":
                  !required,
                "placeholder:text-red-500 dark:placeholder:text-teal-500":
                  required,
              },
              className,
            ])}
            thousandSeparator=","
            decimalSeparator="."
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            inputMode="decimal"
            isAllowed={(values) => {
              const val = Number(values.value);
              return (
                (min === null || val >= min) && (max === null || val <= max)
              );
            }}
          />
        ) : (
          <input
            ref={ref}
            value={value}
            id={id || inputAutoId}
            placeholder={placeholder}
            required={required}
            type={type}
            autoFocus={autoFocus}
            list={list}
            {...(form ? { form } : {})}
            {...(step && type === "number" ? { step } : {})}
            autoComplete={autoComplete}
            className={clsx([
              "dark:text-white",
              "border-solid",
              "border-b-2",
              "text-xs",
              "md:text-sm",
              "w-full",
              "h-[40px]",
              // "rounded",
              "focus:outline-none",
              "placeholder:text-xs",
              "md:placeholder:text-sm",
              "xs",
              "placeholder:font-light",
              "appearance-none",
              "min-w-full",
              "bg-transparent",
              "dark:[color-scheme:dark]",
              "px-1",
              "md:px-2",
              {
                "border-red-200 dark:border-teal-500": required && !value,
                "border-gray-200 dark:border-gray-600":
                  (required && value) || !required,
                "font-light": !fontBold,
                "placeholder:text-gray-500": !required,
                "placeholder:text-red-500": required,
              },
              className,
            ])}
            onChange={onChange}
            disabled={disabled}
            readOnly={readOnly}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e?.key === "Enter") {
                onEnter?.(e);
              }
              onKeyDown?.(e);
            }}
            inputMode={getInputMode() as any}
          />
        )}

        {helpText && (
          <p
            className={clsx([
              "block",
              "text-xs",
              "opacity-50",
              "leading-tight",
              "mt-1",
            ])}
          >
            {helpText}
          </p>
        )}
      </div>
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
